import React, { useState, useRef, useCallback } from 'react';
import { Upload, FileText, Image, File, X, Loader2, Trash2, Download, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { attachmentService } from '../services/api';

const CATEGORIES = [
  { value: 'technical_drawing', label: 'Disegno Tecnico', icon: FileText, color: 'bg-blue-500' },
  { value: 'raw_material_certificate', label: 'Certificato Materia Prima', icon: File, color: 'bg-amber-500' },
  { value: 'cmm_report', label: 'Rapporto CMM', icon: FileText, color: 'bg-violet-500' },
  { value: 'defect_photo', label: 'Foto Difetto', icon: Image, color: 'bg-red-500' },
  { value: 'work_instruction', label: 'Istruzione di Lavoro', icon: FileText, color: 'bg-emerald-500' },
  { value: 'other', label: 'Altro', icon: File, color: 'bg-slate-400' },
];

const getCategoryInfo = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[5];

const formatFileSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImageType = (type) => type && type.startsWith('image/');

const FileIcon = ({ category, fileType, className = "w-8 h-8" }) => {
  if (isImageType(fileType)) return <Image className={`${className} text-pink-500`} />;
  const info = getCategoryInfo(category);
  const Icon = info.icon;
  return <Icon className={`${className} text-slate-500`} />;
};

export const FileUploadZone = ({ entityType, entityId, attachments, onUploadComplete, onDeleteComplete, canEdit = true }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('technical_drawing');
  const [description, setDescription] = useState('');
  const fileInputRef = useRef(null);

  const handleUpload = useCallback(async (files) => {
    if (!files?.length || !entityId) return;
    setUploading(true);
    try {
      for (const file of files) {
        await attachmentService.upload(file, entityType, entityId, selectedCategory, description);
      }
      setDescription('');
      onUploadComplete?.();
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
    }
  }, [entityType, entityId, selectedCategory, description, onUploadComplete]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  }, [handleUpload]);

  const handleDelete = async (id) => {
    try {
      await attachmentService.delete(id);
      onDeleteComplete?.();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const grouped = CATEGORIES.reduce((acc, cat) => {
    const items = attachments.filter(a => a.category === cat.value);
    if (items.length > 0) acc.push({ ...cat, items });
    return acc;
  }, []);

  return (
    <div className="space-y-6" data-testid="file-upload-zone">
      {/* Upload Area */}
      {canEdit && (
        <Card>
          <CardContent className="p-5">
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="flex-1 space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Categoria</Label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="h-11" data-testid="upload-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Descrizione (opzionale)</Label>
                <Input
                  className="h-11"
                  placeholder="Breve descrizione..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  data-testid="upload-description"
                />
              </div>
            </div>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
                ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-dropzone"
            >
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
                  <p className="text-blue-600 font-semibold">Caricamento in corso...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-10 h-10 text-slate-400" />
                  <p className="text-slate-600 font-medium">Trascina i file qui o clicca per sfogliare</p>
                  <p className="text-xs text-slate-400">PDF, immagini, file CAD, fogli di calcolo (max 50MB)</p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                multiple
                onChange={(e) => { handleUpload(e.target.files); e.target.value = ''; }}
                data-testid="upload-input"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Attachments by Category */}
      {grouped.length === 0 && (
        <p className="text-center text-slate-500 py-6">Nessun allegato</p>
      )}
      {grouped.map(group => {
        const CatIcon = group.icon;
        return (
          <Card key={group.value}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded ${group.color} text-white`}>
                  <CatIcon className="w-4 h-4" />
                </span>
                {group.label}
                <Badge variant="secondary" className="ml-1">{group.items.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {group.items.map(att => (
                  <div key={att.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors group" data-testid={`attachment-${att.id}`}>
                    <FileIcon category={att.category} fileType={att.file_type} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{att.file_name}</p>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                        {att.description && <span>{att.description}</span>}
                        <span>{formatFileSize(att.file_size)}</span>
                        {att.uploaded_by_name && <span>by {att.uploaded_by_name}</span>}
                        <span>{att.created_at?.split('T')[0]}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {isImageType(att.file_type) && (
                        <a href={att.file_url} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="icon" className="h-9 w-9"><Eye className="w-4 h-4" /></Button>
                        </a>
                      )}
                      <a href={att.file_url} target="_blank" rel="noopener noreferrer" download>
                        <Button variant="ghost" size="icon" className="h-9 w-9"><Download className="w-4 h-4" /></Button>
                      </a>
                      {canEdit && (
                        <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500 hover:text-red-700" onClick={() => handleDelete(att.id)} data-testid={`delete-attachment-${att.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default FileUploadZone;
